<template>
  <div class="min-h-screen bg-gray-50 py-6">
    <div class="max-w-4xl w-full mx-auto space-y-6 px-4">
      <h1 class="text-2xl font-bold text-gray-900">Share Message Presets</h1>

    <!-- University selector -->
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-1">Select University</label>
      <select v-model="selected_university" @change="onUniChange" class="w-full h-10 px-3 border rounded-lg">
        <option disabled :value="null">Select University</option>
        <option v-for="u in universities" :key="u.id" :value="u">{{ u.name }}</option>
      </select>
    </div>

    <!-- Global default and bulk apply -->
    <div v-if="selected_university" class="bg-white border rounded-md p-4 space-y-3">
      <h2 class="font-semibold text-gray-900">Default message</h2>
      <div class="text-sm text-gray-700">Use ${link} where the URL should be inserted.</div>
      <div>
        <div class="text-xs font-medium text-gray-600 mb-1">Start with a preset</div>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="p in presets"
            :key="p.key"
            type="button"
            class="px-3 py-1.5 rounded-md border text-sm hover:bg-gray-50"
            @click="usePreset(p.key)"
            :title="p.example"
          >
            {{ p.label }}
          </button>
        </div>
      </div>
      <div class="flex items-start gap-3">
        <textarea v-model="defaultMessage" rows="2" class="w-full border rounded p-2" placeholder="e.g. Watch my personalised GraduationClip! ${link}"></textarea>
      </div>
      <div class="flex flex-wrap gap-2 items-center">
        <button class="px-3 py-1.5 border rounded bg-white hover:bg-gray-50 text-gray-800" @click="applyDefaultAll">Apply to all (overwrite)</button>
        <button class="px-3 py-1.5 border rounded bg-white hover:bg-gray-50 text-gray-800" @click="applyDefaultEmpty">Apply to empty only</button>
        <div class="flex items-center gap-2 text-sm">
          <span>Apply to selected:</span>
          <label v-for="p in platforms" :key="p.key" class="inline-flex items-center gap-1">
            <input type="checkbox" v-model="selectedPlatforms[p.key]" /> {{ p.label }}
          </label>
          <button class="ml-2 px-3 py-1.5 border rounded bg-white hover:bg-gray-50 text-gray-800" @click="applyDefaultSelected">Apply</button>
        </div>
      </div>
    </div>

    <div v-if="selected_university" class="grid gap-4 md:grid-cols-12">
      <!-- Sidebar -->
      <aside class="bg-white border rounded-md p-3 md:col-span-3">
        <div class="text-xs font-medium text-gray-600 mb-2">Platforms</div>
        <button
          v-for="p in platforms"
          :key="p.key"
          type="button"
          @click="activePlatform = p.key"
          class="w-full flex items-center gap-2 px-3 py-2 rounded-md text-left mb-1 border"
          :class="activePlatform === p.key ? 'bg-gray-50 border-gray-300' : 'bg-white hover:bg-gray-50 border-transparent'"
        >
          <span class="inline-block w-2.5 h-2.5 rounded-full" :style="{ backgroundColor: platformMeta[p.key].color }"></span>
          <span class="text-sm">{{ platformMeta[p.key].label }}</span>
        </button>
        <div class="mt-4 text-xs text-gray-500">
          <div><span class="font-medium">Tokens:</span> ${link}</div>
        </div>
      </aside>

      <!-- Editor -->
      <section class="bg-white border rounded-md p-4 md:col-span-9">
        <div class="flex items-center justify-between mb-2">
          <div class="flex items-center gap-2">
            <span class="inline-block w-2.5 h-2.5 rounded-full" :style="{ backgroundColor: activeColor }"></span>
            <h2 class="font-semibold text-gray-900">{{ platformMeta[activePlatform].label }}</h2>
          </div>
          <div class="text-xs" v-if="activePlatform==='twitter'" :class="twitterCounterClass">{{ twitterCounterLabel }}</div>
          <div class="text-xs" v-else-if="activePlatform==='sms'" :class="smsCounterClass">{{ smsCounterLabel }}</div>
          <div class="text-xs text-gray-600" v-else>{{ platformNote }}</div>
        </div>

        <div class="flex items-center gap-3 mb-3 text-sm">
          <span class="text-gray-700">Tokens:</span>
          <button class="px-3 py-1.5 border rounded-md hover:bg-gray-50" @click="insertToken(activePlatform, activeField)" :disabled="hasToken(activeText)" aria-label="Insert link token">${link}</button>
          <span class="ml-1" :class="validationClass(activeText)">{{ linkHint(activeText) }}</span>
        </div>

        <textarea v-model="activeText" rows="8" class="w-full border rounded-md p-3 text-base" :placeholder="editorPlaceholder"></textarea>

        <div class="grid md:grid-cols-2 gap-3 mt-3" v-if="activePlatform==='twitter'">
          <div>
            <label class="block text-sm mb-1">Hashtags (comma separated)</label>
            <input v-model="form.twitter.hashtags" class="w-full border rounded p-2" placeholder="e.g. Graduation,ClassOf2025" />
          </div>
          <div>
            <label class="block text-sm mb-1">Related (comma separated)</label>
            <input v-model="form.twitter.related" class="w-full border rounded p-2" placeholder="e.g. MyGraduationClip" />
          </div>
        </div>

        <div class="flex flex-col gap-3 text-sm mt-4">
          <div class="flex flex-wrap gap-2">
            <button class="px-3 py-1.5 border rounded-md hover:bg-gray-50" @click="resetToDefault(activePlatform)">Reset to default</button>
            <button class="px-3 py-1.5 border rounded-md hover:bg-gray-50" @click="clearField(activePlatform, activeField)">Clear</button>
            <button class="px-3 py-1.5 border rounded-md hover:bg-gray-50" @click="toggleCopyFrom">Copy from…</button>
          </div>
          <div v-if="copyFromOpen" class="w-full mt-1">
            <div class="rounded-md border bg-gray-50 p-3 overflow-hidden">
              <div class="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
                <label class="text-gray-700 sm:col-span-2">Source:</label>
                <select v-model="copyFromSelected" class="border rounded-md px-3 py-2 text-gray-800 w-full sm:col-span-6">
                  <option disabled value="">Choose platform</option>
                  <option v-for="opt in copyCandidates" :key="opt.key" :value="opt.key">{{ opt.label }}</option>
                </select>
                <div class="sm:col-span-2">
                  <button class="w-full px-3 py-1.5 border rounded-md bg-white hover:bg-gray-50 text-gray-800 whitespace-nowrap overflow-hidden text-ellipsis" :disabled="!copyFromSelected" @click="applyCopyFrom">Copy</button>
                </div>
                <div class="sm:col-span-2">
                  <button class="w-full px-3 py-1.5 border rounded-md whitespace-nowrap overflow-hidden text-ellipsis" @click="copyFromOpen=false">Cancel</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Live Preview moved below editor -->
      <section class="bg-white border rounded-md p-4 md:col-span-12">
        <div class="flex items-center gap-2 mb-3">
          <div class="w-8 h-8 rounded-full flex items-center justify-center" :style="{ backgroundColor: activeColor, color: 'white' }">{{ universityInitials }}</div>
          <div>
            <div class="text-sm font-semibold text-gray-900">{{ selected_university.name }}</div>
            <div class="text-xs text-gray-500">@{{ selected_university.slug }}</div>
          </div>
        </div>
        <div class="text-sm text-gray-800 leading-relaxed">
          <span v-html="previewHtml(activeText)"></span>
        </div>
      </section>

      <!-- Save Bar -->
      <div class="md:col-span-12 flex items-center gap-3 mt-2">
        <button :disabled="saving" @click="save" class="px-4 py-2.5 rounded-md border border-transparent bg-blue-600 text-white hover:bg-blue-500 text-base">
          {{ saving ? 'Saving…' : 'Save Presets' }}
        </button>
        <span v-if="status" class="text-sm" :class="{ 'text-green-700': status==='Saved', 'text-red-700': status.startsWith('Error') }">{{ status }}</span>
      </div>
    </div>
    </div>
  </div>
  
</template>

<script>
import axios from 'axios'

export default {
  name: 'ShareTemplates',
  data() {
    return {
      universities: [],
      selected_university: null,
      activePlatform: 'linkedin',
      presets: [
        { key: 'proud', label: 'Proud', example: "I'm incredibly proud to share my GraduationClip! ${link}", text: "I'm incredibly proud to share my GraduationClip! ${link}" },
        { key: 'celebratory', label: 'Celebratory', example: 'I did it! 🎓 Check out my GraduationClip: ${link}', text: 'I did it! 🎓 Check out my GraduationClip: ${link}' },
        { key: 'professional', label: 'Professional', example: 'Honoured to share my GraduationClip. ${link}', text: 'Honoured to share my GraduationClip. ${link}' },
        { key: 'gratitude', label: 'Grateful', example: 'Grateful for the support — here’s my GraduationClip. ${link}', text: 'Grateful for the support — here’s my GraduationClip. ${link}' },
        { key: 'concise', label: 'Concise', example: 'My GraduationClip: ${link}', text: 'My GraduationClip: ${link}' },
        { key: 'community', label: 'Community', example: 'Friends & family — my GraduationClip is here! ${link}', text: 'Friends & family — my GraduationClip is here! ${link}' },
      ],
      form: {
        linkedin: { text: '' },
        twitter: { text: '', hashtags: '', related: '' },
        facebook: { text: '' },
        whatsapp: { text: '' },
        email: { text: '' },
        sms: { body: '' },
      },
      saving: false,
      status: '',
      defaultMessage: '',
      selectedPlatforms: { linkedin: false, twitter: false, facebook: false, whatsapp: false, email: false, sms: false },
      exampleLink: 'https://clip.example/your-clip',
      copyFromOpen: false,
      copyFromSelected: '',
    }
  },
  created() {
    axios.get('/universities').then(r => this.universities = r.data)
  },
  computed: {
    platforms() {
      return [
        { key: 'linkedin', label: 'LinkedIn', field: 'text' },
        { key: 'twitter', label: 'Twitter (X)', field: 'text' },
        { key: 'facebook', label: 'Facebook', field: 'text' },
        { key: 'whatsapp', label: 'WhatsApp', field: 'text' },
        { key: 'email', label: 'Email', field: 'text' },
        { key: 'sms', label: 'SMS', field: 'body' },
      ]
    },
    copyCandidates() {
      // List other platforms as copy sources; mark if empty
      return this.platforms
        .filter(p => p.key !== this.activePlatform)
        .map(p => {
          const val = (this.form[p.key] && this.form[p.key][p.field]) || ''
          const label = this.platformMeta[p.key].label + (val && val.trim() !== '' ? '' : ' (empty)')
          return { key: p.key, label }
        })
    },
    platformMeta() {
      return {
        linkedin: { label: 'LinkedIn', color: '#0A66C2' },
        twitter: { label: 'Twitter (X)', color: '#1D9BF0' },
        facebook: { label: 'Facebook', color: '#1877F2' },
        whatsapp: { label: 'WhatsApp', color: '#25D366' },
        email: { label: 'Email', color: '#6B7280' },
        sms: { label: 'SMS', color: '#10B981' },
      }
    },
    activeField() {
      return this.platforms.find(p => p.key === this.activePlatform)?.field || 'text'
    },
    activeText: {
      get() {
        const field = this.activeField
        return (this.form[this.activePlatform] && this.form[this.activePlatform][field]) || ''
      },
      set(val) {
        const field = this.activeField
        if (!this.form[this.activePlatform]) this.$set(this.form, this.activePlatform, {})
        this.$set(this.form[this.activePlatform], field, val)
      }
    },
    activeColor() {
      return (this.platformMeta[this.activePlatform] && this.platformMeta[this.activePlatform].color) || '#6B7280'
    },
    platformNote() {
      switch (this.activePlatform) {
        case 'linkedin': return 'Prefill may be ignored; OG tags drive preview'
        case 'facebook': return 'Prefill may be trimmed; OG tags drive preview'
        case 'whatsapp': return 'Simple text; link shows preview'
        case 'email': return 'Body only'
        default: return ''
      }
    },
    editorPlaceholder() {
      switch (this.activePlatform) {
        case 'linkedin': return "e.g. I'm proud to share my personalised GraduationClip! ${link}"
        case 'twitter': return 'e.g. Proud to share my GraduationClip! ${link}'
        case 'facebook': return 'e.g. Proud to share my GraduationClip! ${link}'
        case 'whatsapp': return 'e.g. Watch my personalised GraduationClip! ${link}'
        case 'email': return 'e.g. Watch my personalised GraduationClip! ${link}'
        case 'sms': return 'e.g. Watch my personalised GraduationClip! ${link}'
        default: return 'Write your message…'
      }
    },
    universityInitials() {
      const name = (this.selected_university && this.selected_university.name) || ''
      if (!name) return 'U'
      const parts = name.split(/\s+/).filter(Boolean)
      return (parts[0]?.[0] || '') + (parts[1]?.[0] || '')
    },
    twitterCounter() {
      // Count ${link} as 23 chars (t.co) per link; rough but practical
      const text = this.form.twitter.text || ''
      const linkCount = (text.match(/\$\{link\}/g) || []).length
      const effective = text.replace(/\$\{link\}/g, '').length + (linkCount * 23)
      return 280 - effective
    },
    twitterCounterLabel() { return `Characters remaining: ${this.twitterCounter}` },
    twitterCounterClass() { return this.twitterCounter < 0 ? 'text-red-600' : 'text-gray-600' },
    smsSegments() {
      const text = this.form.sms.body || ''
      const linkCount = (text.match(/\$\{link\}/g) || []).length
      const effective = text.replace(/\$\{link\}/g, '').length + (linkCount * 23)
      return Math.ceil(Math.max(1, effective) / 160)
    },
    smsCounterLabel() { return `${this.smsSegments} SMS segment${this.smsSegments>1?'s':''}` },
    smsCounterClass() { return this.smsSegments > 3 ? 'text-orange-600' : 'text-gray-600' },
  },
  methods: {
    onUniChange() {
      this.load()
    },
    async load() {
      if (!this.selected_university) return
      this.status = ''
      try {
        const r = await axios.get(`/universities/${this.selected_university.id}/share-templates`)
        const st = r?.data?.shareTemplates || {}
        this.form = {
          linkedin: { text: st?.linkedin?.text || '' },
          twitter: { text: st?.twitter?.text || '', hashtags: st?.twitter?.hashtags || '', related: st?.twitter?.related || '' },
          facebook: { text: st?.facebook?.text || '' },
          whatsapp: { text: st?.whatsapp?.text || '' },
          email: { text: st?.email?.text || '' },
          sms: { body: st?.sms?.body || '' },
        }
        // Suggest a default based on the most common non-empty
        const candidates = [this.form.linkedin.text, this.form.twitter.text, this.form.facebook.text, this.form.whatsapp.text, this.form.email.text, this.form.sms.body].filter(Boolean)
        this.defaultMessage = candidates[0] || ''
      } catch (e) {
        this.status = 'Error loading templates'
      }
    },
    hasToken(s) { return (s || '').includes('${link}') },
    validationClass(s) {
      const t = s || ''
      const count = (t.match(/\$\{link\}/g) || []).length
      if (count === 0) return 'text-orange-600'
      if (count > 1) return 'text-red-600'
      return 'text-gray-600'
    },
    linkHint(s) {
      const t = s || ''
      const count = (t.match(/\$\{link\}/g) || []).length
      if (count === 0) return 'No ${link} detected'
      if (count > 1) return 'Multiple ${link} tokens found'
      return '1 ${link} found'
    },
    insertToken(platform, field) {
      const current = this.form[platform][field] || ''
      if (!this.hasToken(current)) {
        this.form[platform][field] = current + (current && !current.endsWith(' ') ? ' ' : '') + '${link}'
      }
    },
    previewHtml(s) {
      const safe = (s || '').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      return safe.replace(/\$\{link\}/g, `<span class="px-1 rounded bg-blue-100 text-blue-800">${this.exampleLink}</span>`)
    },
    applyDefaultAll() {
      this.platforms.forEach(p => {
        this.form[p.key][p.field] = this.defaultMessage
      })
    },
    applyDefaultEmpty() {
      this.platforms.forEach(p => {
        const val = this.form[p.key][p.field]
        if (!val || val.trim() === '') this.form[p.key][p.field] = this.defaultMessage
      })
    },
    applyDefaultSelected() {
      this.platforms.forEach(p => {
        if (this.selectedPlatforms[p.key]) this.form[p.key][p.field] = this.defaultMessage
      })
    },
    usePreset(key) {
      const p = this.presets.find(x => x.key === key)
      if (!p) return
      this.defaultMessage = p.text
    },
    resetToDefault(platform) {
      const field = this.platforms.find(x => x.key === platform)?.field || 'text'
      this.form[platform][field] = this.defaultMessage
    },
    clearField(platform, field) {
      this.form[platform][field] = ''
    },
    toggleCopyFrom() {
      this.copyFromOpen = !this.copyFromOpen
      if (this.copyFromOpen) this.copyFromSelected = ''
    },
    applyCopyFrom() {
      const src = this.copyFromSelected
      if (!src || src === this.activePlatform) return
      const sfield = this.platforms.find(p => p.key === src)?.field || 'text'
      this.activeText = (this.form[src] && this.form[src][sfield]) || ''
      this.copyFromOpen = false
    },
    async save() {
      if (!this.selected_university) return
      this.saving = true
      this.status = ''
      try {
        // Prepare payload; omit empty keys for cleanliness
        const cleaned = {}
        const entries = Object.entries(this.form)
        entries.forEach(([k, v]) => {
          const sub = {}
          Object.entries(v || {}).forEach(([kk, vv]) => { if ((vv || '').toString().trim() !== '') sub[kk] = vv })
          if (Object.keys(sub).length) cleaned[k] = sub
        })
        await axios.put(`/universities/${this.selected_university.id}/share-templates`, { templates: cleaned })
        this.status = 'Saved'
      } catch (e) {
        this.status = 'Error saving templates'
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.bg-blue-50 { background-color: #eff6ff; }
.text-blue-700 { color: #1d4ed8; }
.bg-blue-100 { background-color: #dbeafe; }
.text-blue-800 { color: #1e40af; }
</style>
